import { DecimalPipe } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Blast } from 'src/app/services/hole.service';
import { BlastEnvironmentService } from 'src/app/services/blast-environment.service';
import { Co2BlastSummary } from 'src/app/services/co2-calculator.service';

@Component({
  selector: 'app-blast-environment',
  templateUrl: './blast-environment.component.html',
  styleUrls: ['./blast-environment.component.scss'],
  standalone: true,
  imports: [TranslateModule, DecimalPipe]
})
export class BlastEnvironmentComponent {
  private readonly blastEnvironmentService = inject(BlastEnvironmentService);

  readonly tirId = input<number>();
  readonly blast = input<Blast>();

  readonly co2Summary = signal<Co2BlastSummary | null>(null);
  readonly isLoading = signal(false);

  constructor() {
    effect(() => {
      const blast = this.blast();
      if (blast?.Holes) {
        this.tryCalculate();
      }
    });

    effect(() => {
      const tirId = this.tirId();
      if (tirId) {
        this.loadData();
      }
    });
  }

  private tryCalculate(): void {
    this.co2Summary.set(this.blastEnvironmentService.calculateSummary(this.blast()));
  }

  private async loadData(): Promise<void> {
    if (!this.tirId()) return;

    this.isLoading.set(true);

    try {
      await this.blastEnvironmentService.loadData();
    } catch (error) {
      console.error('Error loading CO2 data:', error);
    } finally {
      this.isLoading.set(false);
      this.tryCalculate();
    }
  }

  getDonutGradient(): string {
    const summary = this.co2Summary();
    if (!summary || summary.total === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    let cumulativePercentage = 0;
    const segments: string[] = [];

    const categoriesWithValue = summary.categories.filter(cat => cat.value > 0);

    if (categoriesWithValue.length === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    categoriesWithValue.forEach((cat, index) => {
      const isLast = index === categoriesWithValue.length - 1;
      const endPercentage = isLast ? 100 : cumulativePercentage + cat.percentage;
      segments.push(`${cat.color} ${cumulativePercentage}% ${endPercentage}%`);
      cumulativePercentage = endPercentage;
    });

    return `conic-gradient(${segments.join(', ')})`;
  }
}
